import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import {
  fetchSession,
  login as requestLogin,
  logout as requestLogout,
  verifyMfa as requestVerifyMfa,
} from './auth-client'
import type { AuthFailure, AuthResult, LoginOutcome, SessionUser } from './auth-client'

/**
 * De sessie van het dashboard: wie er ingelogd is, en hoe dat verandert.
 *
 * Bij het opstarten is er precies één vraag te beantwoorden — bestaat er al een
 * geldige sessiecookie? Die vraag wordt met `GET /auth/session` gesteld en levert
 * drie toestanden op:
 *
 *   loading        de vraag loopt nog; nog niets tonen en zeker niet doorsturen
 *   authenticated  er is een gebruiker
 *   anonymous      er is er geen (of de API was niet bereikbaar)
 *
 * Die tussentoestand is geen luxe: zonder `loading` stuurt de beschermde route
 * iedereen bij het eerste beeld naar /login, ook wie gewoon ingelogd is.
 *
 * Een mislukte aanroep (API plat, onleesbaar antwoord) telt als `anonymous` mét
 * de fout erbij. Doen alsof iemand ingelogd is terwijl dat niet vaststaat is de
 * verkeerde kant om te falen; de bewaarde fout laat het inlogscherm wél uitleggen
 * dat het aan het platform ligt en niet aan het wachtwoord.
 */

export type SessionState =
  | { readonly phase: 'loading' }
  | { readonly phase: 'authenticated'; readonly user: SessionUser }
  | { readonly phase: 'anonymous'; readonly error?: AuthFailure | undefined }

export interface SessionContextValue {
  readonly state: SessionState
  /** De ingelogde gebruiker, of null zolang die er niet (zeker) is. */
  readonly user: SessionUser | null
  /**
   * true zodra een inlogpoging om de tweede factor vroeg en die nog niet gegeven
   * is. Dit staat bewust náást `state` in plaats van als vierde toestand: de
   * sessie is op dat moment nog niets waard, en `GET /auth/session` geeft er ook
   * geen gebruiker voor terug.
   */
  readonly mfaPending: boolean
  /**
   * true wanneer een platformbeheerder in deze omgeving meekijkt (§17.38).
   *
   * Alleen bij `GET /auth/session` bekend en dus niet meteen na het inloggen — maar
   * inloggen kán ook geen impersonatie zijn, dus die combinatie bestaat niet.
   */
  readonly impersonated: boolean
  readonly login: (email: string, password: string) => Promise<AuthResult<LoginOutcome>>
  readonly verifyMfa: (code: string) => Promise<AuthResult<SessionUser>>
  readonly logout: () => Promise<void>
  /** Haalt de sessie opnieuw op; bedoeld voor herstel na een netwerkfout. */
  readonly refresh: () => Promise<void>
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }): ReactElement {
  const [state, setState] = useState<SessionState>({ phase: 'loading' })
  const [mfaPending, setMfaPending] = useState(false)
  const [impersonated, setImpersonated] = useState(false)
  const controllerRef = useRef<AbortController | null>(null)

  const refresh = useCallback(async (): Promise<void> => {
    // Een lopende aanvraag afbreken voorkomt dat een traag antwoord een nieuwer
    // antwoord overschrijft — hetzelfde patroon als op de systeempagina.
    controllerRef.current?.abort()
    const controller = new AbortController()
    controllerRef.current = controller

    const result = await fetchSession({ signal: controller.signal })
    if (controller.signal.aborted) {
      return
    }
    if (!result.ok) {
      setState({ phase: 'anonymous', error: result.error })
      return
    }
    if (result.data === null) {
      setImpersonated(false)
      setState({ phase: 'anonymous' })
      return
    }
    setMfaPending(false)
    setImpersonated(result.data.impersonated)
    setState({ phase: 'authenticated', user: result.data.user })
  }, [])

  useEffect(() => {
    // Zie de toelichting op hetzelfde patroon in system-page.tsx: `refresh` zet
    // pas state ná de await, de begintoestand is al 'loading', en de cleanup
    // breekt de aanvraag af. De lintregel kan dat statisch niet zien.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh()
    return () => {
      controllerRef.current?.abort()
    }
  }, [refresh])

  const login = useCallback(
    async (email: string, password: string): Promise<AuthResult<LoginOutcome>> => {
      const result = await requestLogin({ email, password })
      if (!result.ok) {
        return result
      }
      if (result.data.kind === 'authenticated') {
        setMfaPending(false)
        // Wie zelf inlogt, wordt per definitie niet bekeken.
        setImpersonated(false)
        setState({ phase: 'authenticated', user: result.data.user })
      } else {
        // De cookie bestaat nu wel, maar met mfa_pending: hij mag nog niets.
        setMfaPending(true)
        setState({ phase: 'anonymous' })
      }
      return result
    },
    [],
  )

  const verifyMfa = useCallback(async (code: string): Promise<AuthResult<SessionUser>> => {
    const result = await requestVerifyMfa(code)
    if (result.ok) {
      setMfaPending(false)
      setState({ phase: 'authenticated', user: result.data })
    }
    return result
  }, [])

  const logout = useCallback(async (): Promise<void> => {
    await requestLogout()
    // Ongeacht de uitkomst lokaal uitloggen. Mislukt de aanroep, dan is de cookie
    // aan de serverkant misschien nog geldig — maar een scherm dat de gebruiker
    // ingelogd houdt terwijl hij op uitloggen drukte, is de slechtere uitkomst.
    setMfaPending(false)
    setImpersonated(false)
    setState({ phase: 'anonymous' })
  }, [])

  const value = useMemo<SessionContextValue>(
    () => ({
      state,
      user: state.phase === 'authenticated' ? state.user : null,
      mfaPending,
      impersonated,
      login,
      verifyMfa,
      logout,
      refresh,
    }),
    [state, mfaPending, impersonated, login, verifyMfa, logout, refresh],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

/** Leest de sessiecontext of werpt wanneer de provider ontbreekt. */
export function useSession(): SessionContextValue {
  const session = useContext(SessionContext)
  if (session === null) {
    throw new Error('useSession must be used inside <SessionProvider>')
  }
  return session
}
